import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, Loader2, CheckCircle, Brain, Zap, Target } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import Navigation from "../../../components/landing/Navigation";
import Footer from "../../../components/landing/Footer";

const ResumeAnalyzer = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState("");
  const [analysisSuccess, setAnalysisSuccess] = useState(false);
  const [resumeHtml, setResumeHtml] = useState(""); // Store HTML for PDF generation

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const apiUrl = import.meta.env.VITE_API_PORT;

  const features = [
    {
      icon: <Brain className="w-8 h-8 text-[#79e708]" />,
      title: "AI Resume Analysis",
      description: "Smart algorithms parse and rank resumes automatically",
    },
    {
      icon: <Target className="w-8 h-8 text-[#79e708]" />,
      title: "Smart Match Accuracy",
      description: "Precise candidate-job matching with ML algorithms",
    },
    {
      icon: <Zap className="w-8 h-8 text-[#79e708]" />,
      title: "Lightning Fast Ranking",
      description: "Process hundreds of resumes in seconds",
    },
  ];

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setError("File too large. Maximum size is 10MB.");
        setSelectedFile(null);
        event.target.value = "";
        return;
      }

      const fileExtension = file.name.toLowerCase().split(".").pop();
      if (fileExtension === "pdf" || fileExtension === "docx") {
        setSelectedFile(file);
        setError("");
      } else {
        setError("Please upload a PDF or DOCX file");
        setSelectedFile(null);
        event.target.value = "";
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedFile) {
      setError("Please select a file");
      return;
    }

    setIsLoading(true);
    setError("");
    setAnalysisSuccess(false);

    const formData = new FormData();
    formData.append("resume", selectedFile);

    try {
      const response = await fetch(`${apiUrl}/api/resume/analyze`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        sessionStorage.setItem("analysisResult", JSON.stringify(result.data));
        // Assuming result.data.html contains resume HTML for PDF generation
        if (result.data.html) setResumeHtml(result.data.html);
        setAnalysisSuccess(true);
        navigate("/analyzer-result");
      } else {
        setError(result.error || "Analysis failed. Please try again.");
      }
    } catch (err) {
      console.error("Analysis error:", err);
      if (err.name === "TypeError" && err.message.includes("fetch")) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError(err.message || "Failed to analyze resume. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeneratePdf = async () => {
    setIsDownloading(true);
    setError("");
    try {
      if (!resumeHtml) {
        setError("No resume HTML available for PDF generation.");
        setIsDownloading(false);
        return;
      }

      const response = await fetch(`${apiUrl}/api/resume/generate-resume-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ html: resumeHtml }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate PDF: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "enhanced_resume.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF generation/download error:", err);
      setError("Failed to download resume PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-black relative overflow-hidden mt-16">
        <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
          {/* Heading */}
          <div className="flex justify-center mb-8">
            <h1 className="flex items-center gap-4 text-6xl lg:text-7xl font-black tracking-tight">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#79e708] to-[#5bb406] rounded-2xl shadow-2xl shadow-[#79e708]/30">
                <span className="text-3xl font-black text-black">AI</span>
              </div>
              <span className="bg-gradient-to-r from-white via-[#79e708] to-white bg-clip-text text-transparent">
                Resume Analyzer
              </span>
            </h1>
          </div>

          <p className="text-center text-gray-400 mb-12">
            Get your resume ATS-ready and stand out with AI-powered insights.
          </p>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {features.map((f, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05 }}
                className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6 text-center shadow-lg"
              >
                <div className="flex justify-center mb-4">{f.icon}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-gray-400 text-sm">{f.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Upload Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <label
              htmlFor="resumeUpload"
              className="cursor-pointer border-2 border-dashed border-[#79e708]/50 rounded-2xl px-6 py-10 flex flex-col items-center justify-center gap-4 text-center hover:bg-[#79e708]/5 transition-all"
            >
              <UploadCloud className="w-10 h-10 text-[#79e708]" />
              <span className="text-white font-semibold text-lg">Click to upload your resume</span>
              <span className="text-sm text-gray-400">
                Accepted formats: <strong>PDF</strong>, <strong>DOCX</strong> (Max 10MB)
              </span>
              <Input id="resumeUpload" type="file" accept=".pdf,.docx" onChange={handleFileChange} className="hidden" />
            </label>

            {selectedFile && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 text-white flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#79e708]" />
                {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </motion.p>
            )}

            <AnimatePresence>
              {error && (
                <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="mt-4 text-red-500 font-semibold">
                  ⚠️ {error}
                </motion.p>
              )}
            </AnimatePresence>

            <Button
              type="submit"
              disabled={!selectedFile || isLoading}
              className="mt-6 bg-gradient-to-r from-[#79e708] to-[#5bb406] hover:brightness-110 text-black font-semibold px-6 py-2 rounded-full shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...
                </>
              ) : (
                <>🚀 Analyze My Resume</>
              )}
            </Button>
          </motion.form>

          {/* Download PDF Button */}
          {analysisSuccess && (
            <div className="text-center mt-8">
              <Button
                onClick={handleGeneratePdf}
                disabled={isDownloading}
                className="bg-gradient-to-r from-[#79e708] to-[#5bb406] hover:brightness-110 text-black font-semibold px-6 py-2 rounded-full shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Downloading PDF...
                  </>
                ) : (
                  <>📥 Download Resume PDF</>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ResumeAnalyzer;
